import React, { useState, useEffect } from "react";
import axios from "axios";
import Menu from "../Menu";
import { useNavigate, useParams } from "react-router-dom";

const SubGroupData = () => {
  const { subgroupId, groupId } = useParams();
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileType, setFileType] = useState(""); // "svg", "csv", or "excel"
  const [extractedData, setExtractedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState(""); // success or error

  const navigate = useNavigate();
  const token = getCookie("token");
  const userid = getCookie("userid");

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type === "image/svg+xml") {
        setSelectedFile(file);
        setFileType("svg");
        setMessage("");
      } else if (file.type === "text/csv" || file.name.endsWith(".csv")) {
        setSelectedFile(file);
        setFileType("csv");
        setMessage("");
      } else if (
        file.name.endsWith(".xlsx") ||
        file.name.endsWith(".xls") ||
        file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel"
      ) {
        setSelectedFile(file);
        setFileType("excel");
        setMessage("");
      } else {
        setMessage("Please select a valid SVG, CSV, or Excel file");
        setMessageType("error");
        setSelectedFile(null);
        setFileType("");
      }
    }
  };

  const extractCSVData = async () => {
    try {
      const text = await selectedFile.text();
      const lines = text.trim().split("\n");

      if (lines.length < 2) {
        setMessage("CSV file appears to be empty");
        setMessageType("error");
        return;
      }

      const data = [];

      // Process each line (skip header if present)
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue; // Skip empty lines

        // Parse CSV line (basic parsing, handles quoted values)
        const values = line.split(",").map((val) => val.trim().replace(/^"|"$/g, ""));

        if (values.length >= 2) {
          data.push({
            merchantid: values[0] || "",
            storeid: values.length >= 3 ? values[1] : "",
            posname: values.length >= 3 ? values[2] : values[1] || "",
            status: "", // Initially blank
            msg: "", // Initially blank
            remark: "", // Initially blank
            active: true,
            apiResponse: null, // Store full API response
          });
        }
      }

      if (data.length === 0) {
        setMessage("No valid records found in CSV");
        setMessageType("error");
        return;
      }

      setExtractedData(data);
      setMessage(`CSV data extracted successfully! ${data.length} records found.`);
      setMessageType("success");
    } catch (error) {
      console.error("Error extracting CSV:", error);
      setMessage("Error extracting CSV data. Please check the file format.");
      setMessageType("error");
    }
  };

  const extractSVGData = async () => {
    if (!selectedFile) {
      setMessage("Please select an SVG file first");
      setMessageType("error");
      return;
    }

    setLoading(true);
    try {
      const text = await selectedFile.text();
      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(text, "image/svg+xml");

      // Extract text elements from SVG
      const textElements = svgDoc.querySelectorAll("text, tspan");
      const data = [];
      let posName = "";
      let merchantName = "";
      let status = "";
      let msg = "";

      // Group consecutive text elements into records
      let currentRecord = {};
      let recordIndex = 0;
      let skipCount = 0; // Skip first 2 header elements (Merchant Name, Pos Name)

      textElements.forEach((element, index) => {
        const textContent = element.textContent.trim();
        if (textContent) {
          // Skip the first 2 header elements (header row)
          if (skipCount < 2) {
            skipCount++;
            return;
          }

          // Extract Merchant Name, Store ID (if available), then POS Name
          if (recordIndex === 0) {
            currentRecord.merchantid = textContent;
            recordIndex++;
          } else if (recordIndex === 1) {
            currentRecord.storeid = textContent;
            recordIndex++;
          } else if (recordIndex === 2) {
            currentRecord.posname = textContent;
            // Complete record
            data.push({
              ...currentRecord,
              status: "",
              msg: "",
              remark: "",
              active: true,
              apiResponse: null,
            });
            currentRecord = {};
            recordIndex = 0;
          }
        }
      });

      if (data.length === 0) {
        // Alternative: Try to extract from tspan elements with x and y positioning
        const tspans = svgDoc.querySelectorAll("tspan");
        let extractedRecords = [];
        let currentData = {};
        let skipHeaderCount = 0; // Skip first 2 header elements

        tspans.forEach((tspan) => {
          const text = tspan.textContent.trim();
          if (text) {
            // Skip the first 2 header elements (header row)
            if (skipHeaderCount < 2) {
              skipHeaderCount++;
              return;
            }

            if (!currentData.merchantid) {
              currentData.merchantid = text;
            } else if (!currentData.posname) {
              currentData.posname = text;
              // Complete record with blank status and message
              extractedRecords.push({
                ...currentData,
                status: "", // Initially blank
                msg: "", // Initially blank
                remark: "", // Initially blank
                active: true,
                apiResponse: null, // Store full API response
              });
              currentData = {};
            }
          }
        });

        setExtractedData(extractedRecords);
      } else {
        setExtractedData(data);
      }

      setMessage("SVG data extracted successfully!");
      setMessageType("success");
    } catch (error) {
      console.error("Error extracting SVG:", error);
      setMessage("Error extracting SVG data. Please check the file format.");
      setMessageType("error");

    } finally {
      setLoading(false);
    }
  };

  const extractExcelData = async () => {
    try {
      // Load XLSX if not available
      if (window.XLSX === undefined) {
        const script = document.createElement("script");
        script.src = "https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js";
        document.head.appendChild(script);
        await new Promise((resolve) => (script.onload = resolve));
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = window.XLSX.read(data, { type: "array" });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = window.XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length < 2) {
          setMessage("Excel file appears to be empty");
          setMessageType("error");
          return;
        }

        const extracted = [];
        // Skip header
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (row && row.length >= 2) {
            extracted.push({
              merchantid: String(row[0] || "").trim(),
              storeid: row.length >= 3 ? String(row[1] || "").trim() : "",
              posname: row.length >= 3 ? String(row[2] || "").trim() : String(row[1] || "").trim(),
              status: "",
              msg: "",
              remark: "",
              active: true,
              apiResponse: null,
            });
          }
        }

        if (extracted.length === 0) {
          setMessage("No valid records found in Excel");
          setMessageType("error");
          return;
        }

        setExtractedData(extracted);
        setMessage(`Excel data extracted successfully! ${extracted.length} records found.`);
        setMessageType("success");
      };
      reader.readAsArrayBuffer(selectedFile);
    } catch (error) {
      console.error("Error extracting Excel:", error);
      setMessage("Error extracting Excel data. Please check the file format.");
      setMessageType("error");
    }
  };

  const handleDownloadSample = (type) => {
    if (type === 'csv') {
      const csvContent = "Merchant ID,Store ID,POS Name\nMERCH001,STORE_01,POS_01\nMERCH002,STORE_02,POS_02";
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", "sample_subgroup_data.csv");
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (type === 'excel') {
      // Create a simple CSV-like string for Excel if XLSX isn't loaded yet, 
      // but better to just use a Blob with the right MIME type for a simple CSV
      // or if they have a real static file, point to it. 
      // Since I can't easily generate a real XLSX without the library, 
      // I'll just use the CSV content with .csv extension but labelled as Excel 
      // OR I can use the XLSX library if it's already loaded.

      const generateExcel = async () => {
        if (window.XLSX === undefined) {
          const script = document.createElement("script");
          script.src = "https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js";
          document.head.appendChild(script);
          await new Promise((resolve) => (script.onload = resolve));
        }

        const ws_data = [
          ["Merchant ID", "Store ID", "POS Name"],
          ["MERCH001", "STORE_01", "POS_01"],
          ["MERCH002", "STORE_02", "POS_02"]
        ];
        const ws = window.XLSX.utils.aoa_to_sheet(ws_data);
        const wb = window.XLSX.utils.book_new();
        window.XLSX.utils.book_append_sheet(wb, ws, "SampleData");
        window.XLSX.writeFile(wb, "sample_subgroup_data.xlsx");
      };

      generateExcel();
    }
  };

  const handleExtractData = async () => {
    setLoading(true);
    try {
      if (fileType === "csv") {
        await extractCSVData();
      } else if (fileType === "svg") {
        await extractSVGData();
      } else if (fileType === "excel") {
        await extractExcelData();
      } else {
        setMessage("Unknown file type");
        setMessageType("error");
      }
    } finally {
      setLoading(false);
    }
  };

  const submitDataToAPI = async () => {
    if (extractedData.length === 0) {
      setMessage("No data to submit");
      setMessageType("error");
      return;
    }

    setUploading(true);
    let successCount = 0;
    let errorCount = 0;
    const updatedData = [...extractedData];

    try {
      for (let i = 0; i < extractedData.length; i++) {
        const record = extractedData[i];
        try {
          const payload = {
            subgroupid: parseInt(subgroupId),
            posname: record.posname || "",
            merchantid: record.merchantid || "",
            storeid: record.storeid || "",
            remark: record.remark || record.msg || "",
            active: record.active || true,
          };

          const response = await axios.post(
            "https://api.dqr-display-tms.eventum.co.in/api/SubGroupDataOperation/Insert",
            payload,
            {
              headers: {
                "X-Authorization": token,
                UserId: userid,
                "Content-Type": "application/json-patch+json",
                accept: "text/plain",
              },
            }
          );

          // Update Status and Message/Remark with API response
          updatedData[i].apiResponse = response.data; // Store full API response
          updatedData[i].status = "Success";
          updatedData[i].msg = response.data?.message || "Record submitted successfully";
          updatedData[i].remark = response.data?.message || "Record submitted successfully";

          // Update the state to reflect changes one by one
          setExtractedData([...updatedData]);

          successCount++;
        } catch (error) {
          console.error(`Error submitting record ${i + 1}:`, error);

          // Update Status and Message/Remark with error information
          updatedData[i].apiResponse = error.response?.data; // Store full API response (error)
          updatedData[i].status = "Failed";
          updatedData[i].msg = error.response?.data?.errormsg || error.response?.data?.message || error.message || "Error submitting record";
          updatedData[i].remark = error.response?.data?.errormsg || error.response?.data?.message || error.message || "Error submitting record";

          // Update the state to reflect changes one by one
          setExtractedData([...updatedData]);

          errorCount++;
        }
      }

      setMessage(
        `Data submitted successfully! Success: ${successCount}, Failed: ${errorCount}`
      );
      setMessageType("success");

      // Reset form after successful submission
      setTimeout(() => {
        navigate(`/subgroup/${groupId}`);
      }, 20000);
    } catch (error) {
      console.error("Error submitting data:", error);
      setMessage("Error submitting data to API");
      setMessageType("error");
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <Menu />
      <div className="wrapper">
        <div className="content-wrapper">
          <section className="content-header">
            <div className="container-fluid">
              <div className="d-flex justify-content-between align-items-center w-100">
                <div>
                  <h1 className="mb-0">Upload Data CSV</h1>
                  {/* <p className="text-muted">SubGroup: <strong>{subgroupName}</strong></p> */}
                </div>
                <div>

                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => navigate(`/subgroup/${subgroupId}`)}
                  >
                    <i className="fas fa-arrow-left mr-1"></i>Back to SubGroup
                  </button>
                </div>
              </div>
            </div>
          </section>

          <section className="content">
            <div className="container-fluid">
              <div className="row">
                <div className="col-lg-8">
                  <div className="card">
                    <div className="card-header">
                      <h3 className="card-title">File Upload CSV</h3>
                    </div>
                    <div className="card-body">
                      <div className="form-group">
                        <label htmlFor="svgFile">Select File:</label>
                        <input
                          type="file"
                          className="form-control"
                          id="svgFile"
                          accept=".svg,.csv,.xlsx,.xls"
                          onChange={handleFileChange}
                          disabled={loading || uploading}
                        />
                        <div className="mt-2 d-flex justify-content-between align-items-center">
                          <small className="form-text text-muted">
                            Upload CSV or Excel file. Columns: Merchant ID, Store ID, POS Name
                          </small>
                          <div>
                            <button
                              className="btn btn-xs btn-link p-0 mr-2"
                              onClick={() => handleDownloadSample('csv')}
                              type="button"
                            >
                              <i className="fas fa-download mr-1"></i>Sample CSV
                            </button>
                            <button
                              className="btn btn-xs btn-link p-0"
                              onClick={() => handleDownloadSample('excel')}
                              type="button"
                            >
                              <i className="fas fa-download mr-1"></i>Sample Excel
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="form-group">
                        <button
                          className="btn btn-info"
                          onClick={handleExtractData}
                          disabled={!selectedFile || loading || uploading}
                        >
                          {loading ? (
                            <>
                              <span className="spinner-border spinner-border-sm mr-2"></span>
                              Extracting...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-file-import mr-2"></i>
                              Extract Data
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Extracted Data Table */}
              {extractedData.length > 0 && (
                <div className="row mt-3">
                  <div className="col-12">
                    <div className="card">
                      <div className="card-header">
                        <h3 className="card-title">
                          Extracted Data ({extractedData.length} records)
                        </h3>
                      </div>
                      <div className="card-body table-responsive p-0">
                        <table className="table table-hover table-striped table-sm">
                          <thead className="thead-light" style={{
                            backgroundColor: 'var(--surface2)',
                            color: 'var(--text)',
                            borderColor: 'var(--border)'
                          }}>
                            <tr>
                              <th style={{ width: "3%" }} className="text-center">
                                #
                              </th>
                              <th style={{ width: "15%" }}>Merchant ID</th>
                              <th style={{ width: "15%" }}>Store ID</th>
                              <th style={{ width: "15%" }}>POS Name</th>
                              <th style={{ width: "10%" }}>Status</th>
                              <th style={{ width: "42%" }}>API Response / Message</th>
                            </tr>
                          </thead>
                          <tbody>
                            {extractedData.map((record, index) => (
                              <tr key={index}>
                                <td className="text-center">{index + 1}</td>
                                <td>{record.merchantid || "-"}</td>
                                <td>{record.storeid || "-"}</td>
                                <td>{record.posname || "-"}</td>
                                <td>
                                  <span
                                    className={`badge ${record.status === "Success"
                                      ? "badge-success"
                                      : record.status === "Failed"
                                        ? "badge-danger"
                                        : "badge-secondary"
                                      }`}
                                  >
                                    {record.status || "Pending"}
                                  </span>
                                </td>
                                <td>
                                  <div style={{ fontSize: "20px", lineHeight: "1.4" }}>
                                    {record.apiResponse ? (
                                      <div>
                                        <br />
                                        {/* <strong>Message:</strong>{" "} */}
                                        {record.apiResponse?.errormsg ||
                                          record.apiResponse?.message ||
                                          record.msg ||
                                          "-"}

                                      </div>
                                    ) : (
                                      record.msg || record.remark || "-"
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="card-footer">
                        <button
                          className="btn btn-success"
                          onClick={submitDataToAPI}
                          disabled={uploading}
                        >
                          {uploading ? (
                            <>
                              <span className="spinner-border spinner-border-sm mr-2"></span>
                              Submitting...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-cloud-upload-alt mr-2"></i>
                              Submit Data to Database
                            </>
                          )}
                        </button>
                        <button
                          className="btn btn-secondary ml-2"
                          onClick={() => {
                            setExtractedData([]);
                            setSelectedFile(null);
                            document.getElementById("svgFile").value = "";
                          }}
                          disabled={uploading}
                        >
                          <i className="fas fa-redo mr-2"></i>
                          Reset
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

      {/* Message display */}
      {message && (
        <div
          style={{
            position: "fixed",
            top: "20px",
            right: "10px",
            width: "25%",
            zIndex: "9999",
          }}
        >
          <div
            className={`alert ${messageType === "success" ? "alert-success" : "alert-danger"
              } d-flex align-items-center`}
            role="alert"
            style={{ marginBottom: "10px" }}
          >
            <div>
              {messageType === "success" ? (
                <i className="fas fa-check-circle mr-2"></i>
              ) : (
                <i className="fas fa-exclamation-circle mr-2"></i>
              )}
              {message}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SubGroupData;

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}

